import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_SERVICE_URL || 'https://api.imanager.extratime.cloud';

const api = axios.create({
  baseURL: API_BASE_URL,
});

export interface Ad {
  id: number;
  tx_ad_name: string;
  tx_display_text: string;
  tx_display_data: string;
  tx_ad_data?: string;
  tx_match_id?: string;
  client_field_values?: Record<string, any>;
  fk_client: number;
  fk_bundle_ad?: number;
  bundle_ad?: BundleAd;
}

export interface Client {
  id: number;
  tx_name: string;
  tx_client_secret: string;
  tx_operators_space_link?: string;
  ads: Ad[];
  bundles?: Bundle[];
}

export interface BundleField {
  id: number;
  fk_bundle: number;
  tx_name: string;
  tx_client_field_name?: string;
  tx_description: string;
  ar_options?: any[];
}

export interface BundleAd {
  id: number;
  fk_bundles: number;
  tx_name: string;
  bundle?: Bundle;
}

export interface Bundle {
  id: number;
  tx_name: string;
  tx_version: string;
  tx_client_bundle_name?: string;
  bundle_fields: BundleField[];
  bundle_ads: BundleAd[];
  clients?: ClientPublic[];
}

export interface ClientPublic {
  id: number;
  tx_name: string;
  tx_operators_space_link?: string;
  ads: Ad[];
}

export const apiService = {
  async getAds(clientSecret: string): Promise<Ad[]> {
    const response = await api.get('/ads/', {
      params: { client_secret: clientSecret }
    });
    return response.data;
  },

  async getAdDetails(clientName: string, adName: string, clientSecret: string, matchId?: string): Promise<Ad> {
    const params: any = { client_secret: clientSecret };
    if (matchId) {
      params.match_id = matchId;
    }
    const response = await api.get(`/${clientName}/${adName}`, {
      params
    });
    return response.data;
  },

  async validateClientSecret(clientSecret: string): Promise<boolean> {
    try {
      await api.get('/ads/', {
        params: { client_secret: clientSecret }
      });
      return true;
    } catch (error) {
      return false;
    }
  },

  async getClientBySecret(clientSecret: string): Promise<Client> {
    const response = await api.get('/client-by-secret/', {
      params: { client_secret: clientSecret }
    });
    return response.data;
  },

  async updateAdDisplayText(adName: string, clientSecret: string, data: any, matchId?: string): Promise<Ad> {
    const params: any = { client_secret: clientSecret };
    if (matchId) {
      params.match_id = matchId;
    }
    const response = await api.patch(`/ads/${adName}/display_text`, data, {
      params
    });
    return response.data;
  },

  async updateAdDisplayData(adName: string, clientSecret: string, data: any, matchId?: string): Promise<Ad> {
    const params: any = { client_secret: clientSecret };
    if (matchId) {
      params.match_id = matchId;
    }
    const response = await api.patch(`/ads/${adName}/display_data`, data, {
      params
    });
    return response.data;
  },

  async getAdByName(adName: string, clientSecret: string, matchId?: string): Promise<Ad> {
    const params: any = { client_secret: clientSecret };
    if (matchId) {
      params.match_id = matchId;
    }
    const response = await api.get(`/ads/${adName}`, {
      params
    });
    return response.data;
  },

  async setAdData(adName: string, clientSecret: string, data: any, matchId?: string): Promise<Ad> {
    const params: any = { client_secret: clientSecret };
    if (matchId) {
      params.match_id = matchId;
    }
    const response = await api.post(`/set-ad-data/${adName}`, data, {
      params
    });
    return response.data;
  },

  // Admin endpoints
  async getAllClients(adminSecret: string): Promise<Client[]> {
    const response = await api.get('/clients/', {
      params: { admin_secret: adminSecret }
    });
    return response.data;
  },

  async createClient(
    adminSecret: string, 
    clientName: string,
    operatorSpaceLink?: string,
    bundleIds?: number[]
  ): Promise<{ id: number; tx_name: string; tx_client_secret: string }> {
    const response = await api.post('/clients/', {
      tx_name: clientName,
      tx_operators_space_link: operatorSpaceLink || null,
      bundle_ids: bundleIds || []
    }, {
      params: { admin_secret: adminSecret }
    });
    return response.data;
  },

  async updateClientSecret(adminSecret: string, clientId: number, newSecret: string): Promise<{ id: number; tx_name: string; tx_client_secret: string }> {
    const response = await api.patch(`/clients/${clientId}/secret`, {
      new_secret: newSecret
    }, {
      params: { admin_secret: adminSecret }
    });
    return response.data;
  },

  async updateClient(
    adminSecret: string, 
    clientId: number, 
    operatorSpaceLink?: string,
    bundleIds?: number[]
  ): Promise<Client> {
    const response = await api.patch(`/clients/${clientId}`, {
      tx_operators_space_link: operatorSpaceLink,
      bundle_ids: bundleIds
    }, {
      params: { admin_secret: adminSecret }
    });
    return response.data;
  },

  // Bundle endpoints
  async getAllBundles(adminSecret: string): Promise<Bundle[]> {
    const response = await api.get('/bundles/', {
      params: { admin_secret: adminSecret }
    });
    return response.data;
  },

  async createBundle(adminSecret: string, bundleData: { tx_name: string; tx_version: string; tx_client_bundle_name?: string; }): Promise<Bundle> {
    const response = await api.post('/bundles/', bundleData, {
      params: { admin_secret: adminSecret }
    });
    return response.data;
  },

  async getBundle(adminSecret: string, bundleId: number): Promise<Bundle> {
    const response = await api.get(`/bundles/${bundleId}`, {
      params: { admin_secret: adminSecret }
    });
    return response.data;
  },

  async createBundleField(adminSecret: string, bundleId: number, fieldData: { tx_name: string; tx_client_field_name?: string; tx_description: string; ar_options?: any[] }): Promise<BundleField> {
    const response = await api.post(`/bundles/${bundleId}/fields/`, fieldData, {
      params: { admin_secret: adminSecret }
    });
    return response.data;
  },

  async getBundleFields(adminSecret: string, bundleId: number): Promise<BundleField[]> {
    const response = await api.get(`/bundles/${bundleId}/fields/`, {
      params: { admin_secret: adminSecret }
    });
    return response.data;
  },

  async updateBundleField(adminSecret: string, bundleId: number, fieldId: number, fieldData: { tx_name: string; tx_client_field_name?: string; tx_description: string; ar_options?: any[] }): Promise<BundleField> {
    const response = await api.put(`/bundles/${bundleId}/fields/${fieldId}`, fieldData, {
      params: { admin_secret: adminSecret }
    });
    return response.data;
  },

  async deleteBundleField(adminSecret: string, bundleId: number, fieldId: number): Promise<void> {
    await api.delete(`/bundles/${bundleId}/fields/${fieldId}`, {
      params: { admin_secret: adminSecret }
    });
  },

  async createBundleAd(adminSecret: string, bundleId: number, adData: { tx_name: string }): Promise<BundleAd> {
    const response = await api.post(`/bundles/${bundleId}/ads/`, adData, {
      params: { admin_secret: adminSecret }
    });
    return response.data;
  },

  async getBundleAds(adminSecret: string, bundleId: number): Promise<BundleAd[]> {
    const response = await api.get(`/bundles/${bundleId}/ads/`, {
      params: { admin_secret: adminSecret }
    });
    return response.data;
  },

  async updateBundleAd(adminSecret: string, bundleId: number, adId: number, adData: { tx_name: string }): Promise<BundleAd> {
    const response = await api.put(`/bundles/${bundleId}/ads/${adId}`, adData, {
      params: { admin_secret: adminSecret }
    });
    return response.data;
  },

  async deleteBundleAd(adminSecret: string, bundleId: number, adId: number): Promise<void> {
    await api.delete(`/bundles/${bundleId}/ads/${adId}`, {
      params: { admin_secret: adminSecret }
    });
  },

  async updateBundle(adminSecret: string, bundleId: number, bundleData: { tx_name: string; tx_version: string; tx_client_bundle_name?: string; }): Promise<Bundle> {
    const response = await api.put(`/bundles/${bundleId}`, bundleData, {
      params: { admin_secret: adminSecret }
    });
    return response.data;
  },

  async deleteBundle(adminSecret: string, bundleId: number): Promise<void> {
    await api.delete(`/bundles/${bundleId}`, {
      params: { admin_secret: adminSecret }
    });
  },

  // Bundle-based Ad endpoints
  async createClientAd(
    clientSecret: string,
    adData: {
      name: string;
      match_id: string;
      bundle_fields: Record<string, any>;
      bundle_ad_id: number;
    }
  ): Promise<Ad> {
    const response = await api.post('/bundle-ads/', adData, {
      params: { client_secret: clientSecret }
    });
    return response.data;
  },

  async updateClientAd(
    clientSecret: string,
    adId: number,
    adData: {
      name?: string;
      match_id: string;
      bundle_fields: Record<string, any>;
      bundle_ad_id?: number;
    }
  ): Promise<Ad> {
    const response = await api.patch(`/bundle-ads/${adId}`, adData, {
      params: { client_secret: clientSecret }
    });
    return response.data;
  },

  async deleteAd(adName: string, clientSecret: string, matchId?: string): Promise<void> {
    const params: any = { client_secret: clientSecret };
    if (matchId) {
      params.match_id = matchId;
    }
    await api.delete(`/ads/${adName}`, {
      params
    });
  }
};